import { useCallback, useContext, useSyncExternalStore } from "react";
import { NatsConnection } from "@nats-io/nats-core";
import { PresenceScope } from "@common/types/presence-nats";
import { ScopeContext } from "@state/scopeContext.lib";
import { useNats } from "contexts/NatsContext";
import { workspaceConnectionPool } from "./workspaceConnectionPool";

const noopUnsubscribe = () => {};

/**
 * The app-wide connection carries the organization's presence permissions and
 * those of the workspace selected in the main scope. Views that render another
 * workspace inside a sub-scope (the organization tasks page) share a pooled
 * connection authorized for just that workspace.
 */
export const usePresenceConnection = (
    scope: PresenceScope | null | undefined,
): NatsConnection | null => {
    const appConnection = useNats();
    const { workspaceId: appWorkspaceId } = useContext(ScopeContext);
    const workspaceId = scope?.workspaceId;
    const pooledWorkspaceId =
        workspaceId && workspaceId !== appWorkspaceId ? workspaceId : null;

    const subscribe = useCallback(
        (onChange: () => void) =>
            pooledWorkspaceId
                ? workspaceConnectionPool.subscribe(pooledWorkspaceId, onChange)
                : noopUnsubscribe,
        [pooledWorkspaceId],
    );
    const getSnapshot = useCallback(
        () =>
            pooledWorkspaceId
                ? workspaceConnectionPool.getConnection(pooledWorkspaceId)
                : null,
        [pooledWorkspaceId],
    );
    const pooledConnection = useSyncExternalStore(subscribe, getSnapshot);

    if (!scope) {
        return null;
    }
    return pooledWorkspaceId ? pooledConnection : (appConnection ?? null);
};
